<script>
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import { cookieConsent, getCookieCategories } from '$lib/cookieConsent.svelte.js';
	import { analytics } from '$lib/analytics.svelte.js';
	import { translateInLang } from '$lib/i18n.svelte.js';
	import { getCurrentPageLanguage, toastDisplay } from '$lib/utils.svelte.js';
	import { fly } from 'svelte/transition';

	/**
	 * @typedef {Object} BannerProps
	 * @property {string} [position] - Banner position: 'bottom-left' (default)
	 * @property {boolean} [showDetailsInitially=false] - Show detailed view by default
	 * @property {Function} [onclose] - Callback when banner is closed
	 */

	/** @type {BannerProps} */
	let { showDetailsInitially = false, onclose } = $props();

	// Reactive state
	let showDetails = $state(showDetailsInitially);
	let isVisible = $state(false);

	// Derived values from cookie consent store
	const showBanner = $derived(cookieConsent.needsBanner && browser);
	const bannerConfig = $derived(cookieConsent.getBannerConfig());

	// Get current page language and create translation helper
	const currentLang = $derived(getCurrentPageLanguage());
	const tPage = $derived((/** @type {string} */ key) => translateInLang(key, currentLang));

	// Get localized cookie categories
	let cookieCategories = $derived(getCookieCategories());

	/**
	 * Handle accept all cookies
	 */
	function handleAcceptAll() {
		cookieConsent.acceptAll();
		analytics.initialize();
		toastDisplay(tPage('cookies.preferencesSuccess'), 'success', 3000);
		hideBanner();
	}

	/**
	 * Handle reject all optional cookies
	 */
	function handleRejectAll() {
		cookieConsent.rejectAll();
		toastDisplay(tPage('cookies.preferencesSuccess'), 'success', 3000);
		hideBanner();
	}

	/**
	 * Handle custom preferences save
	 */
	function handleSavePreferences() {
		const analyticsCheckbox = /** @type {HTMLInputElement|null} */ (
			document.getElementById('analytics-consent')
		);
		const analyticsConsent = analyticsCheckbox?.checked || false;

		cookieConsent.updateConsent({ analytics: analyticsConsent });

		if (analyticsConsent) {
			analytics.initialize();
		}

		toastDisplay(tPage('cookies.preferencesSuccess'), 'success', 3000);
		hideBanner();
	}

	/**
	 * Hide banner with animation
	 */
	function hideBanner() {
		cookieConsent.hideBanner(); // Hide any manually triggered banner
		setTimeout(() => {
			isVisible = false;
			if (onclose) onclose();
		}, 300);
	}

	/**
	 * Toggle detailed view
	 */
	function toggleDetails() {
		showDetails = !showDetails;
	}

	// Initialize banner visibility on mount
	onMount(() => {
		if (showBanner) {
			// Only apply delay if NOT manually triggered (i.e., initial automatic appearance)
			const delay = cookieConsent.manuallyTriggered ? 0 : bannerConfig.timing;
			setTimeout(() => {
				isVisible = true;
			}, delay);
		}
	});

	// Watch for showBanner changes after mount
	$effect(() => {
		if (showBanner && !isVisible) {
			// Banner should appear
			// Only apply delay if NOT manually triggered (i.e., initial automatic appearance)
			const delay = cookieConsent.manuallyTriggered ? 0 : bannerConfig.timing;
			setTimeout(() => {
				isVisible = true;
			}, delay);
		}
	});
</script>

{#if showBanner && isVisible}
	<div
		in:fly={{ y: 20, duration: 400 }}
		out:fly={{ y: 20, duration: 200 }}
		class="cookie-banner fixed bottom-6 left-6 z-50 w-[25rem] rounded-2xl border border-gray-200 bg-white
			shadow-xl transition-all duration-500 ease-out"
		role="dialog"
		aria-labelledby="cookie-banner-title"
		aria-describedby="cookie-banner-description"
	>
		<div class="p-6">
			{#if !showDetails}
				<!-- Simple logo at top-right -->
				<div class="absolute top-0 right-0 m-2">
					<img src="/brand/ORDALIE_LOGO_ICONO_BLEU.svg" alt="Ordalie" class="h-16 w-16" />
				</div>
				<!-- Simple view -->
				<div class="relative pt-10">
					<div>
						<!-- Main content -->
						<h3 id="cookie-banner-title" class="mb-4 text-2xl text-gray-900">
							{tPage('cookies.bannerTitle')}
						</h3>

						<p id="cookie-banner-description" class="mb-4 text-sm leading-relaxed text-gray-800">
							{tPage('cookies.bannerDescription')}
						</p>

						<!-- Simple explanation -->
						<div class="border-ord-border mb-4 rounded-md border p-2 text-sm text-gray-500">
							<p class="font-medium">{tPage('cookies.cookiePurposeTitle')}</p>
							<div>
								{#each tPage('cookies.cookiePurposeList').split(';') as purpose}
									<p class="text-xs">- {purpose.trim()}</p>
								{/each}
							</div>
						</div>

						<!-- Buttons -->
						<div class="flex flex-row justify-between gap-1">
							<button
								onclick={handleRejectAll}
								class="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 hover:border-gray-300 focus:ring-2 focus:ring-gray-400 focus:outline-none"
							>
								{tPage('cookies.rejectButton')}
							</button>
							<button
								onclick={toggleDetails}
								class="flex-1 rounded-lg bg-ord-grey px-4 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 hover:border-gray-300 focus:ring-2 focus:ring-gray-400 focus:outline-none"
							>
								{tPage('cookies.customizeButton')}
							</button>
							<button
								onclick={handleAcceptAll}
								class="flex-1 rounded-lg border border-royal-blue/20 bg-royal-blue/5 px-4 py-2.5 text-sm font-medium text-royal-blue transition-all hover:bg-royal-blue/10 hover:border-royal-blue/40 focus:ring-2 focus:ring-blue-500 focus:outline-none"
							>
								{tPage('cookies.acceptAllButton')}
							</button>
						</div>
					</div>
				</div>
			{:else}
				<!-- Detailed view -->
				<div class="space-y-4">
					<div class="flex items-start justify-between gap-3">
						<!-- <img 
							src="/brand/ORDALIE_LOGO_ICONO_BLEU.svg" 
							alt="Ordalie" 
							class="w-8 h-8 flex-shrink-0"
						/> -->
						<div class="flex items-start gap-3">
							<h3 id="cookie-banner-title" class="text-2xl font-semibold text-gray-900">
								{tPage('cookies.preferencesTitle')}
							</h3>
						</div>
						<button
							onclick={toggleDetails}
							class="rounded text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-blue-500 focus:outline-none"
							aria-label={tPage('cookies.closeDetailsLabel')}
						>
							<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								></path>
							</svg>
						</button>
					</div>

					<div class="space-y-4">
						<!-- Essential cookies -->
						<div class="flex items-start space-x-3 rounded-md bg-gray-50 p-3">
							<input
								type="checkbox"
								id="essential-consent"
								checked
								disabled
								class="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
							/>
							<div class="flex-1">
								<label for="essential-consent" class="text-sm font-medium text-gray-900">
									{tPage('cookies.essentialCookiesTitle')}
								</label>
								<p class="mt-1 text-sm text-gray-600">
									{cookieCategories.essential}
								</p>
							</div>
						</div>

						<!-- Analytics cookies -->
						<div class="flex items-start space-x-3 rounded-md border border-gray-200 p-3">
							<input
								type="checkbox"
								id="analytics-consent"
								checked={cookieConsent.state.analytics}
								class="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
							/>
							<div class="flex-1">
								<label for="analytics-consent" class="text-sm font-medium text-gray-900">
									{tPage('cookies.analyticsCookiesTitle')}
								</label>
								<p class="mt-1 text-sm text-gray-600">
									{cookieCategories.analytics}
								</p>
							</div>
						</div>
					</div>

					<div class="flex flex-col gap-3 pt-4 sm:flex-row">
						<button
							onclick={handleRejectAll}
							class="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 focus:ring-2 focus:ring-gray-400 focus:outline-none"
						>
							{tPage('cookies.rejectAllButton')}
						</button>
						<button
							onclick={handleSavePreferences}
							class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-all duration-200 hover:bg-blue-100 focus:ring-2 focus:ring-blue-400 focus:outline-none"
						>
							{tPage('cookies.savePreferencesButton')}
						</button>
						<button
							onclick={handleAcceptAll}
							class="rounded-lg border border-royal-blue/20 bg-royal-blue/5 px-4 py-2 text-sm font-medium text-royal-blue transition-all duration-200 hover:bg-royal-blue/10 hover:border-royal-blue/40 focus:ring-2 focus:ring-blue-500 focus:outline-none"
						>
							{tPage('cookies.acceptAllButton')}
						</button>
					</div>

					<div class="border-t border-gray-200 pt-2 text-xs text-gray-500">
						{tPage('cookies.privacyPolicyText')}
						<a
							href="/{currentLang}/legal/privacy"
							class="text-blue-600 underline hover:text-blue-800"
						>
							{tPage('cookies.privacyPolicyLink')}
						</a>.
					</div>
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.cookie-banner {
		backdrop-filter: blur(10px);
		-webkit-backdrop-filter: blur(10px);
	}

	@media (max-width: 640px) {
		.cookie-banner {
			left: 1rem !important;
			right: 1rem !important;
			bottom: 1rem !important;
			width: auto !important;
			max-width: calc(100vw - 2rem) !important;
		}
	}

	@media (min-width: 640px) {
		.cookie-banner {
			min-width: 380px;
		}
	}
</style>
